import * as THREE from 'three';
import { CRAFT_BLOCK_TYPE } from '@shared/protocol';
import {
  CRAFT_FACES,
  createCraftFaceMaterial,
  type CraftFace,
} from './coreTextures';

export type { CraftFace };
export { createCraftTextureCanvas } from './coreTextures';

interface Placement {
  x: number;
  y: number;
  z: number;
}

/**
 * Craft-block world cells textured from
 * `client/public/textures/core/craft/{face}.png`.
 */
export class CraftBlockRenderer {
  readonly group = new THREE.Group();

  private readonly placements = new Map<string, Placement>();
  private readonly geometry = new THREE.BoxGeometry(1, 1, 1);
  /** Three.js BoxGeometry order: +X, -X, +Y, -Y, +Z, -Z */
  private readonly materials = CRAFT_FACES.map((face) => createCraftFaceMaterial(face));
  private readonly matrix = new THREE.Matrix4();
  private mesh: THREE.InstancedMesh | null = null;
  private dirty = false;

  setCell(x: number, y: number, z: number, type: number): void {
    const key = `${x},${y},${z}`;
    const changed = type === CRAFT_BLOCK_TYPE
      ? !this.placements.has(key)
      : this.placements.delete(key);
    if (type === CRAFT_BLOCK_TYPE) this.placements.set(key, { x, y, z });
    if (changed) this.dirty = true;
  }

  update(): void {
    if (!this.dirty) return;
    this.dirty = false;
    if (this.mesh) {
      this.group.remove(this.mesh);
      this.mesh.dispose();
      this.mesh = null;
    }
    if (this.placements.size === 0) return;
    const mesh = new THREE.InstancedMesh(
      this.geometry,
      this.materials,
      this.placements.size,
    );
    let index = 0;
    for (const placement of this.placements.values()) {
      this.matrix.makeTranslation(
        placement.x + 0.5,
        placement.y + 0.5,
        placement.z + 0.5,
      );
      mesh.setMatrixAt(index++, this.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.mesh = mesh;
    this.group.add(mesh);
  }
}
